import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface PlanData {
  planName: string;
  price: number;
  description: string;
  duration: "monthly" | "yearly" | "custom";
  features: string[];
  fleetEstimatorReportLimit: number;
  siteEstimatorReportLimit: number;
  isPopular?: boolean;
  isEmbedded?: boolean;
  isWhiteList?: boolean;
  isFleetEstimator?: boolean;
  isSiteEstimator?: boolean;
  mode: "public" | "private";
}

export interface Plan {
  id: string;
  planName: string;
  price: number;
  priceId?: string;
  description: string;
  duration: "monthly" | "yearly" | "custom";
  features: string[];
  fleetEstimatorReportLimit: number; 
  siteEstimatorReportLimit: number; 
  isPopular?: boolean;
  isEmbedded?: boolean;
  isWhiteList?: boolean;
  isFleetEstimator?: boolean;
  isSiteEstimator?: boolean; 
  mode: "public" | "private";
  userId: string | null;
  assignName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlanApiResponse {
  success: boolean;
  message: string;
  data?: {
    plan: Plan;
  };
}

export interface PlansApiResponse {
  success: boolean;
  message: string;
  data?: {
    plans: Plan[];
    pagination: {
      totalPlans: number;
      totalPages: number;
      currentPage: number;
      limit: number;
    };
  };
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// CREATE PLAN ACTION
export const createPlan = createAsyncThunk<
  PlanApiResponse,
  PlanData,
  { rejectValue: string }
>("planManagement/createPlan", async (formData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.post<PlanApiResponse>(
      "/plan-management",
      formData
    );
    if (response.data.success) {
      toast.success(response.data.message || "Plan created successfully");
      return response.data;
    } else {
      toast.error(response.data.message || "Plan creation failed");
      return rejectWithValue(response.data.message || "Plan creation failed");
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as PlanApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET ALL PLANS ACTION (for public and user-specific private plans, with pagination support)
export const getAllPlans = createAsyncThunk<
  PlansApiResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "planManagement/getAllPlans",
  async ({ page, limit }, { rejectWithValue }) => {
    try {
      let query = "";
      if (page || limit) {
        const params = new URLSearchParams();
        if (page) params.append("page", page.toString());
        if (limit) params.append("limit", limit.toString());
        query = `?${params.toString()}`;
      }
      const response = await createApiClient.get<PlansApiResponse>(
        `/plan-management${query}`
      );
      if (response.data.success) {
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to retrieve plans");
        return rejectWithValue(
          response.data.message || "Failed to retrieve plans"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as PlansApiResponse).message || "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET ALL PUBLIC PLANS ACTION (with pagination support)
export const getAllPublicPlans = createAsyncThunk<
  PlansApiResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "planManagement/getAllPublicPlans",
  async ({ page, limit }, { rejectWithValue }) => {
    try {
      let query = "";
      if (page || limit) {
        const params = new URLSearchParams();
        if (page) params.append("page", page.toString());
        if (limit) params.append("limit", limit.toString());
        query = `?${params.toString()}`;
      }
      const response = await createApiClient.get<PlansApiResponse>(
        `/plan-management/public${query}`
      );
      if (response.data.success) {
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to retrieve public plans");
        return rejectWithValue(
          response.data.message || "Failed to retrieve public plans"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as PlansApiResponse).message || "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET ALL PRIVATE PLANS ACTION (with pagination support)
export const getAllPrivatePlans = createAsyncThunk<
  PlansApiResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "planManagement/getAllPrivatePlans",
  async ({ page, limit }, { rejectWithValue }) => {
    try {
      let query = "";
      if (page || limit) {
        const params = new URLSearchParams();
        if (page) params.append("page", page.toString());
        if (limit) params.append("limit", limit.toString());
        query = `?${params.toString()}`;
      }
      const response = await createApiClient.get<PlansApiResponse>(
        `/plan-management/private${query}`
      );
      if (response.data.success) {
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to retrieve private plans"
        );
        return rejectWithValue(
          response.data.message || "Failed to retrieve private plans"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as PlansApiResponse).message || "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET CUSTOMER USER ASSIGN AND PUBLIC PLAN ACTION (with pagination support)
export const getCustomerUserAssignAndPublicPlan = createAsyncThunk<
  PlansApiResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "planManagement/getCustomerUserAssignAndPublicPlan",
  async ({ page, limit }, { rejectWithValue }) => {
    try {
      let query = "";
      if (page || limit) {
        const params = new URLSearchParams();
        if (page) params.append("page", page.toString());
        if (limit) params.append("limit", limit.toString());
        query = `?${params.toString()}`;
      }
      const response = await createApiClient.get<PlansApiResponse>(
        `/plan-management/customer-plans${query}`
      );
      if (response.data.success) {
        return response.data;
      } else {
        toast.error(
          response.data.message ||
            "Failed to retrieve customer assigned and public plans"
        );
        return rejectWithValue(
          response.data.message ||
            "Failed to retrieve customer assigned and public plans"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as PlansApiResponse).message || "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET PLAN BY ID ACTION
export const getPlanById = createAsyncThunk<
  PlanApiResponse,
  string,
  { rejectValue: string }
>("planManagement/getPlanById", async (id, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<PlanApiResponse>(
      `/plan-management/${id}`
    );
    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve plan");
      return rejectWithValue(
        response.data.message || "Failed to retrieve plan"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as PlanApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// UPDATE PLAN ACTION
export const updatePlanById = createAsyncThunk<
  PlanApiResponse,
  { id: string; data: Partial<PlanData> },
  { rejectValue: string }
>(
  "planManagement/updatePlanById",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.put<PlanApiResponse>(
        `/plan-management/${id}`,
        data
      );
      if (response.data.success) {
        toast.success(response.data.message || "Plan updated successfully");
        return response.data;
      } else {
        toast.error(response.data.message || "Plan update failed");
        return rejectWithValue(response.data.message || "Plan update failed");
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as PlanApiResponse).message || "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// DELETE PLAN ACTION
export const deletePlanById = createAsyncThunk<
  PlanApiResponse,
  string,
  { rejectValue: string }
>("planManagement/deletePlanById", async (id, { rejectWithValue }) => {
  try {
    const response = await createApiClient.delete<PlanApiResponse>(
      `/plan-management/${id}`
    );
    if (response.data.success) {
      toast.success(response.data.message || "Plan deleted successfully");
      return response.data;
    } else {
      toast.error(response.data.message || "Plan deletion failed");
      return rejectWithValue(response.data.message || "Plan deletion failed");
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as PlanApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});
